import { useEffect, useRef, useState } from 'react';

import { toast } from 'sonner';
import { SSE } from 'sse.js';

import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig from '@/api/useGetFetchConfig';

interface DroppedField {
  fieldName: string;
  documentCount: number;
}

export interface JobProgressEvent {
  type:
    | 'zip-extract-progress'
    | 'zip-extract-complete'
    | 'relativity-import-progress'
    | 'relativity-import-complete'
    | 'relativity-import-error'
    | 'relativity-import-partial-failure'
    | 'NoActiveJobs'
    | 'InitialState';
  jobId?: string;
  jobType?: 'unzip' | 'relativity';
  processedFiles?: number;
  totalFiles?: number;
  fileName?: string;
  error?: string;
  activeJobs?: any[];
  // Relativity specific fields
  progressStatus?: 'preparing' | 'processing';
  preparingProgress?: number;
  preparingMessage?: string;
  droppedFields?: DroppedField[];
}

export interface ActiveJob {
  id: string;
  type: 'unzip' | 'relativity';
  fileName: string;
  totalFiles: number;
  processedFiles: number;
  status: 'processing' | 'completed' | 'error';
  // Relativity specific fields
  progressStatus?: 'preparing' | 'processing';
  preparingProgress?: number;
  preparingMessage?: string;
}

const MAX_RETRIES = 5;
const BASE_DELAY_MS = 500;

export const useJobProgress = (caseId: string) => {
  const [reconnectTrigger, setReconnectTrigger] = useState(0);
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
  const sseRef = useRef<SSE | null>(null);
  const retryCountRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { fetchConfigGET } = useGetFetchConfig();

  const closeSSE = (shouldReconnect: boolean = true) => {
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
    if (shouldReconnect && retryCountRef.current < MAX_RETRIES) {
      // Exponential backoff: 1s, 2s, 4s, 8s, 16s
      const delay = BASE_DELAY_MS * Math.pow(2, retryCountRef.current);
      retryCountRef.current += 1;

      reconnectTimeoutRef.current = setTimeout(() => {
        setReconnectTrigger((prev) => prev + 1);
      }, delay);
    }
  };

  useEffect(() => {
    if (!caseId) return;

    const setupSSE = () => {
      try {
        const sseUrl = `${APIBaseChronos}/api/storage/job-events/${caseId}`;

        const eventSource = new SSE(sseUrl, {
          headers: Object.fromEntries(fetchConfigGET.headers.entries()),
        });

        eventSource.onmessage = (event: any) => {
          // Reset retry count on successful message - connection is working
          retryCountRef.current = 0;

          try {
            if (!event.data || !event.data.trim()) {
              return;
            }

            const data: JobProgressEvent = JSON.parse(event.data);

            setActiveJobs((prevJobs) => {
              // Handle no active jobs message
              if (data.type === 'NoActiveJobs') {
                return [];
              }

              // Handle initial state message - create placeholder jobs for active imports
              // These will be updated with actual progress in subsequent messages
              if (data.type === 'InitialState' && data.activeJobs) {
                const initialJobs: ActiveJob[] = data.activeJobs.map((job: any) => ({
                  id: job.jobId,
                  type: job.jobType as 'unzip' | 'relativity',
                  fileName: 'Loading...',
                  totalFiles: 0,
                  processedFiles: 0,
                  status: 'processing' as const,
                }));
                return initialJobs;
              }

              // Early return if required fields are missing
              if (!data.jobId || !data.jobType) {
                return prevJobs;
              }

              const existingJobIndex = prevJobs.findIndex((job) => job.id === data.jobId);

              if (data.type === 'zip-extract-progress' || data.type === 'relativity-import-progress') {
                const updatedJob: ActiveJob = {
                  id: data.jobId,
                  type: data.jobType,
                  fileName: data.fileName || 'Unknown',
                  totalFiles: data.totalFiles || 0,
                  processedFiles: data.processedFiles || 0,
                  status: 'processing',
                  // Relativity fields
                  progressStatus: data.progressStatus,
                  preparingProgress: data.preparingProgress,
                  preparingMessage: data.preparingMessage,
                };

                if (existingJobIndex >= 0) {
                  // Update existing job
                  const newJobs = [...prevJobs];
                  newJobs[existingJobIndex] = updatedJob;
                  return newJobs;
                } else {
                  // Add new job
                  return [...prevJobs, updatedJob];
                }
              } else if (
                data.type === 'zip-extract-complete' ||
                data.type === 'relativity-import-complete' ||
                data.type === 'relativity-import-partial-failure'
              ) {
                // Show warnings for dropped fields (schema validation failures)
                if (
                  data.droppedFields !== undefined &&
                  data.droppedFields.length > 0 &&
                  (data.type === 'relativity-import-complete' || data.type === 'relativity-import-partial-failure')
                ) {
                  data.droppedFields.forEach((field) => {
                    toast.warning(
                      `The '${field.fieldName}' field failed validation on ${field.documentCount} document${field.documentCount > 1 ? 's' : ''}`,
                      { duration: 8000 },
                    );
                  });
                }
                return prevJobs.filter((job) => job.id !== data.jobId);
              } else if (data.type === 'relativity-import-error') {
                // Update job status to error
                if (existingJobIndex >= 0) {
                  const newJobs = [...prevJobs];
                  newJobs[existingJobIndex] = {
                    ...newJobs[existingJobIndex],
                    status: 'error',
                  };
                  return newJobs;
                }
              }

              return prevJobs;
            });
          } catch (error) {
            console.error('Error parsing SSE message:', error);
            console.error('Raw event data:', event.data);
            closeSSE();
          }
        };

        eventSource.onerror = (error: any) => {
          console.error('SSE error:', error);
          closeSSE();
        };

        sseRef.current = eventSource;
      } catch (error) {
        console.error('Error setting up SSE:', error);
        closeSSE();
      }
    };

    setupSSE();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, [caseId, reconnectTrigger]);

  return { activeJobs };
};
